import { Inline, Text, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { constants } from '@eti/utils';
import type { Size3d } from '@eti/schema-types';
import { useTranslation } from '@eti/providers';
import { screenReadersOnlyStyles } from '../../../../../utils/styleUtils';
import type { BaggageType } from './models';

const highlightStyles = css`
  background-color: ${vars.colors.signal.caution.tint};
`;

interface BaggageItemProps {
  baggageItem: BaggageType;
  baggageTypeText: string;
  dataTestId: string;
}

const BaggageItem = ({ baggageItem, baggageTypeText, dataTestId }: BaggageItemProps) => {
  const { t } = useTranslation();
  const { unicodeControlCharacters } = constants;
  const { LRE, PDF } = unicodeControlCharacters;
  const baggagePiecesText = (pieces: number) => `${LRE}${pieces} x${PDF}`;
  const baggageWeightText = (weight?: number, weightUnit?: string) =>
    `${LRE}${weight}${weightUnit}${PDF}`;
  const baggageSizeText = (size: Size3d) =>
    `${LRE}(${size.height}x${size.width}x${size.length}cm)${PDF}`;

  return (
    <Inline
      align="start"
      className={baggageItem.hasChangesInPieces ? highlightStyles : ''}
      data-testid={dataTestId}
      role="listitem"
      spacing={4}
    >
      <Text data-testid={`${dataTestId}-pieces`}>{baggagePiecesText(baggageItem.pieces)}</Text>
      {Boolean(baggageItem.weight && baggageItem.weightUnit) && (
        <Text
          className={baggageItem.hasChangesInWeight ? highlightStyles : ''}
          data-testid={`${dataTestId}-weight`}
        >
          {baggageWeightText(baggageItem.weight, baggageItem.weightUnit)}
          {baggageItem.hasChangesInWeight && (
            <span className={screenReadersOnlyStyles}>
              {t('ScreenReader.OrderDetails.Baggage.Drawer.NotSameForAllBounds.Text')}
            </span>
          )}
        </Text>
      )}
      <Text data-testid={`${dataTestId}-type`}>{baggageTypeText}</Text>
      {baggageItem.size && (
        <Text data-testid={`${dataTestId}-size`}>{baggageSizeText(baggageItem.size)}</Text>
      )}
      {baggageItem.hasChangesInPieces && (
        <span className={screenReadersOnlyStyles}>
          {t('ScreenReader.OrderDetails.Baggage.Drawer.NotSameForAllBounds.Text')}
        </span>
      )}
    </Inline>
  );
};

export default BaggageItem;
